import { useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Field } from "@/components/Atoms";
import {
  ArrowLeftRight, Download, Upload, RefreshCw, Save, HardDrive,
  Users, Home, Wand2, AlertTriangle,
} from "lucide-react";

/*
 * World Transfer — admin tab.
 *
 * Five tools backed by /api/world-transfer/* (all admin-gated server-side):
 *   1. Worlds registry (register local paths / upload / download save zips)
 *   2. SFTP pull/push against the servers defined in sftp.yml (save_path key)
 *   3. Player transfers between worlds (staged in memory -> Finalize)
 *   4. Base transfers (writes the local target Level.sav immediately)
 *   5. Pal Editor (IVs / souls / trust / passives / level, PST HP recalc)
 *
 * Write model reminder shown in the header: player transfers + pal edits
 * stage in memory until an explicit save; base transfers + SFTP push write
 * immediately. The target server must be STOPPED for the pull->push window.
 */

const fmtBytes = (n) => {
  if (n > 1e9) return (n / 1e9).toFixed(2) + " GB";
  if (n > 1e6) return (n / 1e6).toFixed(1) + " MB";
  if (n > 1e3) return (n / 1e3).toFixed(0) + " KB";
  return `${n} B`;
};

const STEP_DEFS = [
  ["character", "Character"],
  ["tech_data", "Tech & unlocks"],
  ["inventory", "Inventory"],
  ["guild", "Guild"],
  ["pals", "Pals"],
  ["timestamps", "Sync timestamps"],
];

export default function WorldTransfer() {
  const toast = useToast();

  /* ---------- shared ---------- */
  const [worlds, setWorlds] = useState([]);
  const [log, setLog] = useState("");
  const logRef = useRef(null);
  const appendLog = (t) => setLog((l) => (l ? l + "\n" : "") + (t || "").trim());
  useEffect(() => { if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight; }, [log]);

  const refreshWorlds = async () => {
    try {
      const { data } = await api.get("/world-transfer/worlds");
      setWorlds(data.worlds || []);
    } catch (e) { toast.error(apiError(e)); }
  };

  /* ---------- worlds registry ---------- */
  const [regName, setRegName] = useState("");
  const [regPath, setRegPath] = useState("");
  const [upName, setUpName] = useState("");
  const upFileRef = useRef(null);

  const registerWorld = async () => {
    if (!regName.trim() || !regPath.trim()) return toast.error("Name and path required");
    try {
      const { data } = await api.post("/world-transfer/worlds", { name: regName.trim(), path: regPath.trim() });
      toast.success(`Registered '${data.name}'`);
      setRegName(""); setRegPath("");
      refreshWorlds();
    } catch (e) { toast.error(apiError(e)); }
  };

  const uploadWorld = async () => {
    const f = upFileRef.current?.files?.[0];
    if (!upName.trim() || !f) return toast.error("Upload name and zip required");
    const fd = new FormData();
    fd.append("file", f);
    try {
      toast.info?.("Uploading…");
      const { data } = await api.post(`/world-transfer/upload/${encodeURIComponent(upName.trim())}`, fd);
      toast.success(`Uploaded and registered '${data.name}'`);
      setUpName(""); if (upFileRef.current) upFileRef.current.value = "";
      refreshWorlds();
    } catch (e) { toast.error(apiError(e)); }
  };

  const downloadWorld = (name) => {
    // axios for the auth header, blob for the zip
    api.get(`/world-transfer/download/${encodeURIComponent(name)}`, { responseType: "blob" })
      .then(({ data }) => {
        const url = URL.createObjectURL(data);
        const a = document.createElement("a");
        a.href = url; a.download = `${name}_save.zip`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      })
      .catch((e) => toast.error(apiError(e)));
  };

  /* ---------- SFTP ---------- */
  const [sftpWorlds, setSftpWorlds] = useState([]);
  const [job, setJob] = useState(null);       // live job dict
  const jobTimer = useRef(null);

  const refreshSftp = async () => {
    try {
      const { data } = await api.get("/world-transfer/sftp/worlds");
      setSftpWorlds(data.worlds || []);
    } catch (e) { toast.error(apiError(e)); }
  };

  const pollJob = (jobId, verb, slug) => {
    clearInterval(jobTimer.current);
    jobTimer.current = setInterval(async () => {
      try {
        const { data: j } = await api.get(`/world-transfer/sftp/job/${jobId}`);
        setJob({ ...j, verb });
        if (j.state === "done" || j.state === "error") {
          clearInterval(jobTimer.current);
          if (j.state === "done") toast.success(`${verb} '${slug}' complete`);
          else toast.error(`${verb} '${slug}' failed: ${j.error}`);
          (j.log || []).slice(-3).forEach(appendLog);
          refreshSftp(); refreshWorlds();
        }
      } catch (e) {
        clearInterval(jobTimer.current);
        setJob(null);
        toast.error(apiError(e));
      }
    }, 1000);
  };

  const sftpPull = async (slug) => {
    try {
      const { data } = await api.post(`/world-transfer/sftp/pull/${encodeURIComponent(slug)}`);
      setJob({ state: "pending", verb: "Pull", world: slug, files_done: 0, files_total: 0, bytes_done: 0 });
      pollJob(data.job_id, "Pull", slug);
    } catch (e) { toast.error(apiError(e)); }
  };

  const sftpPush = async (slug) => {
    if (!window.confirm(
      `Push local copy of '${slug}' back to the remote server?\n\n` +
      "The remote server MUST be stopped. Remote Level.sav/LevelMeta.sav " +
      "are backed up as *.bak_<timestamp> first."
    )) return;
    try {
      const { data } = await api.post(`/world-transfer/sftp/push/${encodeURIComponent(slug)}`);
      setJob({ state: "pending", verb: "Push", world: slug, files_done: 0, files_total: 0, bytes_done: 0 });
      pollJob(data.job_id, "Push", slug);
    } catch (e) { toast.error(apiError(e)); }
  };

  const jobBusy = job && (job.state === "pending" || job.state === "running");

  /* ---------- player transfers ---------- */
  const [srcWorld, setSrcWorld] = useState("");
  const [tgtWorld, setTgtWorld] = useState("");
  const [session, setSession] = useState(null);        // {srcPlayers, tgtPlayers, srcName, tgtName}
  const [selSrc, setSelSrc] = useState(null);
  const [selTgt, setSelTgt] = useState(null);
  const [srcFilter, setSrcFilter] = useState("");
  const [tgtFilter, setTgtFilter] = useState("");
  const [steps, setSteps] = useState(Object.fromEntries(STEP_DEFS.map(([k]) => [k, true])));
  const [pending, setPending] = useState(0);
  const [busy, setBusy] = useState(false);

  const loadSession = async () => {
    if (!srcWorld || !tgtWorld) return toast.error("Pick both worlds");
    if (srcWorld === tgtWorld) return toast.error("Source and target must differ");
    setBusy(true);
    try {
      const { data } = await api.post("/world-transfer/load", { source: srcWorld, target: tgtWorld });
      setSession({
        srcPlayers: data.source.players, tgtPlayers: data.target.players,
        srcName: srcWorld, tgtName: tgtWorld,
      });
      setSelSrc(null); setSelTgt(null); setPending(0);
      toast.success(`Loaded — ${data.source.players.length} source / ${data.target.players.length} target players`);
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const runTransfer = async () => {
    if (!selSrc) return;
    setBusy(true);
    try {
      const { data } = await api.post("/world-transfer/transfer", {
        source_uid: selSrc, target_uid: selTgt, steps,
      });
      appendLog(data.log);
      if (data.success) {
        setPending(data.pending.length);
        setSelSrc(null); setSelTgt(null);
        toast.success(`Transfer staged in memory (${data.pending.length} pending) — nothing on disk yet`);
      } else toast.error("Transfer failed — see log");
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const finalize = async () => {
    if (!window.confirm("Write all pending transfers into the target world's save files?\nA backup of the target is made first.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/world-transfer/finalize");
      appendLog(data.log);
      if (data.success) {
        setPending(0);
        toast.success(`Saved. Backup: ${data.backup}. Push the world (server stopped) when ready.`);
      } else toast.error("Finalize reported errors — see log");
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  /* ---------- base transfers ---------- */
  const [bases, setBases] = useState([]);
  const [tgtGuilds, setTgtGuilds] = useState([]);
  const [selBase, setSelBase] = useState(null);
  const [selGuild, setSelGuild] = useState("");
  const [baseNames, setBaseNames] = useState({ src: null, tgt: null });
  const [delWorkers, setDelWorkers] = useState(false);

  const runBaseDelete = async () => {
    const b = bases.find((x) => x.base_id === selBase);
    if (!b) return;
    if (!window.confirm(
      `PERMANENTLY DELETE base '${b.name}' (${b.structures} structures, guild: ${b.guild_name || "—"}) from '${baseNames.src}'?\n\n` +
      "Everything goes: structures, chests AND their contents, work assignments. " +
      (delWorkers ? "Worker pals will be DELETED too." : "Worker pals are detached (kept in the world).") +
      "\n\nThis writes the local Level.sav immediately (a backup is taken first) and resets any loaded player session. This cannot be undone except by restoring the backup."
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post("/world-transfer/bases/delete", {
        world: baseNames.src, base_id: selBase, delete_workers: delWorkers,
      });
      appendLog(data.log);
      if (data.success) {
        toast.success(`Base deleted. Backup: ${data.backup}. ${data.note}`);
        setBases((list) => list.filter((x) => x.base_id !== selBase));
        setSelBase(null);
        setSession(null); setPending(0);
      } else toast.error("Base delete failed — see log");
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const loadBases = async () => {
    if (!srcWorld || !tgtWorld) return toast.error("Pick source and target worlds first");
    if (srcWorld === tgtWorld) return toast.error("Source and target must differ");
    setBusy(true);
    try {
      const [{ data: s }, { data: t }] = await Promise.all([
        api.get(`/world-transfer/bases/${encodeURIComponent(srcWorld)}`),
        api.get(`/world-transfer/bases/${encodeURIComponent(tgtWorld)}`),
      ]);
      setBases(s.bases || []); setTgtGuilds(t.guilds || []);
      setSelBase(null); setSelGuild(t.guilds?.[0]?.guild_id || "");
      setBaseNames({ src: srcWorld, tgt: tgtWorld });
      toast.success(`${s.bases.length} bases in '${srcWorld}' — ${t.guilds.length} guilds in '${tgtWorld}'`);
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const runBaseTransfer = async () => {
    const b = bases.find((x) => x.base_id === selBase);
    if (!b || !selGuild) return;
    if (!window.confirm(
      `Copy base '${b.name}' (${b.structures} structures) from '${baseNames.src}' into '${baseNames.tgt}'?\n\n` +
      "This writes the target world's local Level.sav IMMEDIATELY and resets any loaded player session. " +
      "A backup + the .pstbase blueprint are saved first."
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post("/world-transfer/bases/transfer", {
        source: baseNames.src, target: baseNames.tgt,
        base_id: selBase, target_guild_id: selGuild,
      });
      appendLog(data.log);
      if (data.success) {
        toast.success(`Base transferred. ${data.note}`);
        setSession(null); setPending(0); setSelBase(null);
      } else toast.error("Base transfer failed — see log");
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  /* ---------- pal editor ---------- */
  const [peWorld, setPeWorld] = useState("");
  const [pePlayers, setPePlayers] = useState([]);
  const [pePlayer, setPePlayer] = useState("");
  const [pePals, setPePals] = useState([]);
  const [peFilter, setPeFilter] = useState("");
  const [peSel, setPeSel] = useState(null);           // instance_id
  const [peRef, setPeRef] = useState(null);           // reference (passives, limits)
  const [peDirty, setPeDirty] = useState(0);
  const [peForm, setPeForm] = useState(null);         // editable form state

  const peLoad = async () => {
    if (!peWorld) return toast.error("Pick a world");
    setBusy(true);
    try {
      if (!peRef) {
        const { data: ref } = await api.get("/world-transfer/paleditor/reference");
        setPeRef(ref);
      }
      const { data } = await api.post(`/world-transfer/paleditor/load/${encodeURIComponent(peWorld)}`);
      setPePlayers(data.players || []);
      setPePals([]); setPeSel(null); setPeForm(null); setPeDirty(0);
      if (data.players.length) {
        setPePlayer(data.players[0].uid);
        await peLoadPals(data.players[0].uid);
      }
      toast.success(`Loaded '${peWorld}' — ${data.players.length} players`);
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const peLoadPals = async (uid) => {
    try {
      const { data } = await api.get(`/world-transfer/paleditor/pals/${encodeURIComponent(uid)}`);
      setPePals(data.pals || []); setPeSel(null); setPeForm(null);
    } catch (e) { toast.error(apiError(e)); }
  };

  const peSelect = (p) => {
    setPeSel(p.instance_id);
    setPeForm({
      nickname: p.nickname, level: p.level, gender: p.gender === "Female" ? "Female" : "Male",
      iv_hp: p.ivs.hp, iv_shot: p.ivs.shot, iv_def: p.ivs.defense,
      so_hp: p.souls.hp, so_atk: p.souls.attack, so_def: p.souls.defence, so_craft: p.souls.craftspeed,
      rank: p.rank, friendship: p.friendship, lucky: p.is_lucky,
      passives: [0, 1, 2, 3].map((i) => p.passives[i]?.asset || ""),
    });
  };

  const peApply = async (overrides) => {
    if (!peSel) return;
    const f = peForm;
    const changes = overrides || {
      nickname: f.nickname,
      level: parseInt(f.level) || 1,
      gender: f.gender,
      ivs: { hp: +f.iv_hp || 0, shot: +f.iv_shot || 0, defense: +f.iv_def || 0 },
      souls: { hp: +f.so_hp || 0, attack: +f.so_atk || 0, defence: +f.so_def || 0, craftspeed: +f.so_craft || 0 },
      rank: +f.rank || 0,
      friendship: +f.friendship || 0,
      lucky: f.lucky,
      passives: f.passives.filter(Boolean),
    };
    setBusy(true);
    try {
      const { data } = await api.post("/world-transfer/paleditor/edit", { instance_id: peSel, changes });
      setPePals((list) => list.map((x) => (x.instance_id === peSel ? data.pal : x)));
      setPeDirty(data.dirty_pals);
      peSelect(data.pal);
      toast.success(`Applied — HP recalculated to ${data.pal.hp}. ${data.dirty_pals} pal(s) pending save.`);
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const peMax = () => {
    if (!peRef) return;
    const L = peRef.limits;
    peApply({
      level: L.level_max,
      ivs: { hp: L.iv_max, shot: L.iv_max, defense: L.iv_max, melee: L.iv_max },
      souls: { hp: L.soul_max, attack: L.soul_max, defence: L.soul_max, craftspeed: L.soul_max },
      rank: L.rank_max, friendship: L.friendship_max, lucky: true, awakened: true,
    });
  };

  const peSave = async () => {
    if (!window.confirm(`Write all pending pal edits into '${peWorld}' Level.sav?\nA backup is made first. Any loaded transfer session for this world resets.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/world-transfer/paleditor/save");
      appendLog(data.log);
      if (data.success) {
        setPeDirty(0);
        toast.success(`Saved. Backup: ${data.backup}. Push via SFTP when ready (server stopped).`);
      } else toast.error("Save reported errors — see log");
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  /* ---------- boot ---------- */
  useEffect(() => {
    refreshWorlds();
    refreshSftp();
    return () => clearInterval(jobTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const worldOptions = worlds.map((w) => (
    <option key={w.name} value={w.name}>{w.name}{w.exists ? "" : " (missing!)"}</option>
  ));

  const playerRow = (p, sel, onClick) => (
    <tr
      key={p.uid}
      onClick={onClick}
      className={`cursor-pointer transition-colors ${sel ? "bg-[rgba(64,196,180,0.15)]" : "hover:bg-white/5"} ${p.has_sav ? "" : "opacity-40"}`}
      title={p.uid}
    >
      <td className="py-1.5 px-2">{p.name || "(unnamed)"}{p.has_sav ? "" : " ⚠"}</td>
      <td className="py-1.5 px-2">{p.level}</td>
      <td className="py-1.5 px-2">{p.pals}</td>
      <td className="py-1.5 px-2">{p.guild_name || "—"}</td>
      <td className="py-1.5 px-2 text-xs text-[var(--muted)]">{p.last_seen}</td>
    </tr>
  );

  const thead = (
    <thead>
      <tr className="text-left text-[10px] uppercase text-[var(--muted)] border-b border-white/10">
        <th className="py-1 px-2">Name</th><th className="py-1 px-2">Lvl</th>
        <th className="py-1 px-2">Pals</th><th className="py-1 px-2">Guild</th>
        <th className="py-1 px-2">Last seen</th>
      </tr>
    </thead>
  );

  return (
    <div className="space-y-4 fade-up" data-testid="world-transfer-tab">
      {/* header */}
      <div className="glass-card p-4">
        <h3 className="text-lg mb-1 flex items-center gap-2" style={{ fontFamily: "'Cinzel Decorative', serif" }}>
          <ArrowLeftRight size={18} /> World Transfer
        </h3>
        <p className="text-xs text-[var(--muted)]">
          Move players and bases between servers, and edit pals at save level — powered by PalworldSaveTools running headlessly.
          <strong> Player transfers and pal edits stage in memory until you Finalize/Save; base transfers and SFTP Push write immediately.</strong>{" "}
          The target server must be <strong>stopped</strong> for the whole pull→push window — changes appear on its next boot.
        </p>
      </div>

      {/* worlds + sftp */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="glass-card p-5 space-y-3" data-testid="wt-worlds-card">
          <h3 className="label flex items-center gap-2"><HardDrive size={14} /> Worlds</h3>
          <table className="w-full text-sm">
            <tbody>
              {worlds.length === 0 && <tr><td className="text-xs text-[var(--muted)] py-2">No worlds registered — pull one via SFTP or register a path.</td></tr>}
              {worlds.map((w) => (
                <tr key={w.name} className="border-b border-white/5">
                  <td className="py-1.5 pr-2">{w.name}{w.exists ? "" : " (missing!)"}</td>
                  <td className="py-1.5 text-right">
                    <button className="btn btn-sm" onClick={() => downloadWorld(w.name)} title="Download zip">
                      <Download size={12} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
            <Field label="Name"><input className="input" value={regName} onChange={(e) => setRegName(e.target.value)} placeholder="dev-world" /></Field>
            <Field label="Server path to save folder"><input className="input font-mono text-xs" value={regPath} onChange={(e) => setRegPath(e.target.value)} placeholder="/path/to/SaveGames/0/WORLDID" /></Field>
            <button className="btn" onClick={registerWorld}>Register</button>
          </div>
          <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
            <Field label="Upload zip as"><input className="input" value={upName} onChange={(e) => setUpName(e.target.value)} placeholder="name" /></Field>
            <Field label="Save folder zip"><input ref={upFileRef} type="file" accept=".zip" className="input text-xs" /></Field>
            <button className="btn" onClick={uploadWorld}><Upload size={14} /></button>
          </div>
        </div>

        <div className="glass-card p-5 space-y-3" data-testid="wt-sftp-card">
          <h3 className="label flex items-center gap-2"><RefreshCw size={14} /> Remote Servers (sftp.yml)</h3>
          <p className="text-[11px] text-[var(--muted)]">
            Servers come from <code>sftp.yml</code> — each entry's <code>save_path</code> must point at the folder containing Level.sav.
            <strong> Stop the remote server before pushing.</strong>
          </p>
          <table className="w-full text-sm">
            <tbody>
              {sftpWorlds.map((w) => (
                <tr key={w.slug} className="border-b border-white/5">
                  <td className="py-1.5 pr-2">
                    {w.name}
                    {!w.configured && (
                      <span className="ml-2 text-[10px] text-amber-400 inline-flex items-center gap-1">
                        <AlertTriangle size={10} /> {w.reason}
                      </span>
                    )}
                    {w.local_copy && <span className="ml-2 text-[10px] text-[var(--teal)]">local ✔</span>}
                  </td>
                  <td className="py-1.5 text-right whitespace-nowrap">
                    <button className="btn btn-sm mr-1" disabled={jobBusy || !w.configured} onClick={() => sftpPull(w.slug)}>⬇ Pull</button>
                    <button className="btn btn-sm" disabled={jobBusy || !w.configured || !w.local_copy} onClick={() => sftpPush(w.slug)}>⬆ Push</button>
                  </td>
                </tr>
              ))}
              {sftpWorlds.length === 0 && <tr><td className="text-xs text-[var(--muted)] py-2">No servers found in sftp.yml.</td></tr>}
            </tbody>
          </table>
          {job && (
            <div className="text-xs">
              <div className="mb-1">
                {job.verb} '{job.world}' — {job.files_done}/{job.files_total} files, {fmtBytes(job.bytes_done)}
                {job.current_file ? ` (${job.current_file})` : ""} — <strong>{job.state}</strong>
              </div>
              <div className="h-1.5 bg-white/10 rounded overflow-hidden">
                <div
                  className="h-full bg-[var(--teal)] transition-all"
                  style={{ width: `${job.files_total ? Math.round((job.files_done / job.files_total) * 100) : 0}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* player transfer */}
      <div className="glass-card p-5 space-y-3" data-testid="wt-player-card">
        <h3 className="label flex items-center gap-2"><Users size={14} /> Player Transfer</h3>
        <div className="grid md:grid-cols-[1fr_1fr_auto] gap-2 items-end">
          <Field label="Source world (player comes FROM)">
            <select className="input" value={srcWorld} onChange={(e) => setSrcWorld(e.target.value)}>
              <option value="">— pick —</option>{worldOptions}
            </select>
          </Field>
          <Field label="Target world (player goes TO)">
            <select className="input" value={tgtWorld} onChange={(e) => setTgtWorld(e.target.value)}>
              <option value="">— pick —</option>{worldOptions}
            </select>
          </Field>
          <button className="btn" disabled={busy} onClick={loadSession}>
            {busy ? "Working…" : "Load Session"}
          </button>
        </div>

        {session && (
          <>
            <div className="grid lg:grid-cols-2 gap-4">
              {[
                ["Source players", session.srcPlayers, srcFilter, setSrcFilter, selSrc,
                  (p) => p.has_sav ? setSelSrc(selSrc === p.uid ? null : p.uid) : toast.error("That player has no .sav file — cannot transfer")],
                ["Target players (optional — leave unselected to keep the same UID)", session.tgtPlayers, tgtFilter, setTgtFilter, selTgt,
                  (p) => setSelTgt(selTgt === p.uid ? null : p.uid)],
              ].map(([title, list, filter, setFilter, sel, onPick]) => (
                <div key={title}>
                  <div className="text-xs text-[var(--muted)] mb-1">{title} <span className="opacity-60">({list.length})</span></div>
                  <input className="input mb-2" placeholder="Filter…" value={filter} onChange={(e) => setFilter(e.target.value)} />
                  <div className="max-h-64 overflow-y-auto">
                    <table className="w-full text-sm">{thead}
                      <tbody>
                        {list.filter((p) => !filter || (p.name + p.uid + p.guild_name).toLowerCase().includes(filter.toLowerCase()))
                          .map((p) => playerRow(p, sel === p.uid, () => onPick(p)))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
              {STEP_DEFS.map(([k, label]) => (
                <label key={k} className="flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" checked={steps[k]} onChange={(e) => setSteps({ ...steps, [k]: e.target.checked })} />
                  {label}
                </label>
              ))}
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              <button className="btn" disabled={!selSrc || busy} onClick={runTransfer}>Run Transfer (in memory)</button>
              <button className="btn btn-danger" disabled={pending === 0 || busy} onClick={finalize}>
                <Save size={14} className="inline mr-1" />Finalize — Write Target World ({pending} pending)
              </button>
              <span className="text-xs text-[var(--muted)]">
                {selSrc ? `Source: ${selSrc.slice(0, 8)}…` : "Source: none"} → {selTgt ? `Target: ${selTgt.slice(0, 8)}…` : "same UID (new arrival)"}
              </span>
            </div>
          </>
        )}
      </div>

      {/* base transfer */}
      <div className="glass-card p-5 space-y-3" data-testid="wt-base-card">
        <h3 className="label flex items-center gap-2"><Home size={14} /> Base Transfer</h3>
        <p className="text-[11px] text-[var(--muted)]">
          Copies a whole base — structures, chest contents, worker pals, work assignments — at its original map location
          (auto-shifted if occupied). <strong>Writes the local target world to disk immediately</strong>; finalize player transfers first.
          PST skips Pal/Item Booths and loose eggs.
        </p>
        <div className="flex flex-wrap gap-2 items-end">
          <button className="btn" disabled={busy} onClick={loadBases}>Load Bases (uses worlds picked above)</button>
          <Field label="Target guild (new owner)">
            <select className="input" value={selGuild} onChange={(e) => setSelGuild(e.target.value)}>
              {tgtGuilds.map((g) => (
                <option key={g.guild_id} value={g.guild_id}>
                  {g.name || "(unnamed guild)"} — {g.players} players, {g.bases} bases
                </option>
              ))}
            </select>
          </Field>
          <button className="btn btn-danger" disabled={!selBase || !selGuild || busy} onClick={runBaseTransfer}>
            Transfer Base → Target
          </button>
          <label className="flex items-center gap-1.5 cursor-pointer text-xs pb-2">
            <input type="checkbox" checked={delWorkers} onChange={(e) => setDelWorkers(e.target.checked)} />
            delete worker pals too
          </label>
          <button className="btn btn-danger" disabled={!selBase || busy} onClick={runBaseDelete}
                  title="Permanently removes the selected base from the SOURCE world (structures, chest contents, work data)">
            🗑 Delete Base from Source
          </button>
        </div>
        {bases.length > 0 && (
          <div className="max-h-56 overflow-y-auto overflow-x-auto">
            <table className="w-full min-w-[520px] text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase text-[var(--muted)] border-b border-white/10">
                  <th className="py-1 px-2">Base</th><th className="py-1 px-2">Guild</th>
                  <th className="py-1 px-2">Structures</th><th className="py-1 px-2">Map coords</th>
                </tr>
              </thead>
              <tbody>
                {bases.map((b) => (
                  <tr
                    key={b.base_id}
                    onClick={() => setSelBase(selBase === b.base_id ? null : b.base_id)}
                    className={`cursor-pointer ${selBase === b.base_id ? "bg-[rgba(64,196,180,0.15)]" : "hover:bg-white/5"}`}
                    title={b.base_id}
                  >
                    <td className="py-1.5 px-2">{b.name}</td>
                    <td className="py-1.5 px-2">{b.guild_name || "—"}</td>
                    <td className="py-1.5 px-2">{b.structures}</td>
                    <td className="py-1.5 px-2 font-mono text-xs">{b.map_coords}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* pal editor */}
      <div className="glass-card p-5 space-y-3" data-testid="wt-paleditor-card">
        <h3 className="label flex items-center gap-2"><Wand2 size={14} /> Pal Editor</h3>
        <div className="grid md:grid-cols-[1fr_auto_1fr_auto] gap-2 items-end">
          <Field label="World">
            <select className="input" value={peWorld} onChange={(e) => setPeWorld(e.target.value)}>
              <option value="">— pick —</option>{worldOptions}
            </select>
          </Field>
          <button className="btn" disabled={busy} onClick={peLoad}>Load World</button>
          <Field label="Player">
            <select className="input" value={pePlayer} onChange={(e) => { setPePlayer(e.target.value); peLoadPals(e.target.value); }}>
              {pePlayers.map((p) => (
                <option key={p.uid} value={p.uid}>{p.name || "(unnamed)"} — Lv {p.level} [{p.guild_name}]</option>
              ))}
            </select>
          </Field>
          <button className="btn btn-danger" disabled={peDirty === 0 || busy} onClick={peSave}>
            <Save size={14} className="inline mr-1" />Save World ({peDirty})
          </button>
        </div>

        <div className="grid lg:grid-cols-2 gap-4">
          <div>
            <input className="input mb-2" placeholder="Filter pals…" value={peFilter} onChange={(e) => setPeFilter(e.target.value)} />
            <div className="max-h-72 overflow-y-auto overflow-x-auto">
              <table className="w-full min-w-[480px] text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase text-[var(--muted)] border-b border-white/10">
                    <th className="py-1 px-2">Pal</th><th className="py-1 px-2">Lvl</th>
                    <th className="py-1 px-2">Where</th><th className="py-1 px-2">Trust</th><th className="py-1 px-2">Psv</th>
                  </tr>
                </thead>
                <tbody>
                  {pePals
                    .filter((p) => !peFilter || (p.name + p.nickname + p.character_id).toLowerCase().includes(peFilter.toLowerCase()))
                    .map((p) => (
                      <tr
                        key={p.instance_id}
                        onClick={() => peSelect(p)}
                        className={`cursor-pointer ${peSel === p.instance_id ? "bg-[rgba(64,196,180,0.15)]" : "hover:bg-white/5"}`}
                        title={p.character_id}
                      >
                        <td className="py-1.5 px-2">
                          {(p.nickname ? `${p.nickname} (${p.name})` : p.name)}{p.dirty ? " ✏️" : ""}{p.is_lucky ? " ✨" : ""}
                        </td>
                        <td className="py-1.5 px-2">{p.level}</td>
                        <td className="py-1.5 px-2">{p.where}</td>
                        <td className="py-1.5 px-2">{p.trust_rank} <span className="text-[10px] text-[var(--muted)]">({p.friendship})</span></td>
                        <td className="py-1.5 px-2" title={p.passives.map((x) => x.name).join(", ")}>{p.passives.length}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>

          {peForm && peRef && (
            <div className="space-y-2 text-sm">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <Field label="Nickname"><input className="input" value={peForm.nickname} onChange={(e) => setPeForm({ ...peForm, nickname: e.target.value })} /></Field>
                <Field label={`Level (1–${peRef.limits.level_max})`}><input className="input" value={peForm.level} onChange={(e) => setPeForm({ ...peForm, level: e.target.value })} /></Field>
                <Field label="Gender">
                  <select className="input" value={peForm.gender} onChange={(e) => setPeForm({ ...peForm, gender: e.target.value })}>
                    <option>Male</option><option>Female</option>
                  </select>
                </Field>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[["iv_hp", "IV HP"], ["iv_shot", "IV ATK"], ["iv_def", "IV DEF"]].map(([k, l]) => (
                  <Field key={k} label={`${l} (0–${peRef.limits.iv_max})`}>
                    <input className="input" value={peForm[k]} onChange={(e) => setPeForm({ ...peForm, [k]: e.target.value })} />
                  </Field>
                ))}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[["so_hp", "Soul HP"], ["so_atk", "Soul ATK"], ["so_def", "Soul DEF"], ["so_craft", "Soul Work"]].map(([k, l]) => (
                  <Field key={k} label={`${l} (0–${peRef.limits.soul_max})`}>
                    <input className="input" value={peForm[k]} onChange={(e) => setPeForm({ ...peForm, [k]: e.target.value })} />
                  </Field>
                ))}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 items-end">
                <Field label={`Stars (0–${peRef.limits.rank_max})`}><input className="input" value={peForm.rank} onChange={(e) => setPeForm({ ...peForm, rank: e.target.value })} /></Field>
                <Field label={`Trust (0–${peRef.limits.friendship_max})`}><input className="input" value={peForm.friendship} onChange={(e) => setPeForm({ ...peForm, friendship: e.target.value })} /></Field>
                <label className="flex items-center gap-1.5 cursor-pointer pb-2">
                  <input type="checkbox" checked={peForm.lucky} onChange={(e) => setPeForm({ ...peForm, lucky: e.target.checked })} /> Lucky ✨
                </label>
              </div>
              <Field label="Passives (up to 4)">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[0, 1, 2, 3].map((i) => (
                    <select
                      key={i}
                      className="input"
                      value={peForm.passives[i]}
                      onChange={(e) => {
                        const ps = [...peForm.passives]; ps[i] = e.target.value;
                        setPeForm({ ...peForm, passives: ps });
                      }}
                    >
                      <option value="">— none —</option>
                      {peRef.passives.map((pv) => (
                        <option key={pv.asset} value={pv.asset} title={pv.description}>{pv.name}</option>
                      ))}
                    </select>
                  ))}
                </div>
              </Field>
              <div className="flex flex-wrap gap-2">
                <button className="btn" disabled={busy} onClick={() => peApply()}>Apply (in memory)</button>
                <button className="btn" disabled={busy} onClick={peMax}>Max This Pal</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* log */}
      {log && (
        <div className="glass-card p-4">
          <h3 className="label mb-2">Operation Log</h3>
          <pre ref={logRef} className="text-[10px] bg-black/40 rounded p-3 overflow-auto max-h-64 whitespace-pre-wrap">{log}</pre>
        </div>
      )}
    </div>
  );
}
